import React, { useEffect, useRef, useState } from 'react';
import { FiCheck, FiImage, FiSave, FiX, FiZap } from 'react-icons/fi';
import api from '../../services/api';
import {
  occupiedInstagramMinutesOnDay,
  suggestPublishTimeForDay,
  todayIsoAmsterdam,
} from '../../utils/instagramScheduleSlots';
import '../../components/Button.css';
import './MarketingInstagram.css';

function captionFromProposal(proposal) {
  const copy = proposal?.ai_copy_json || {};
  const meta = String(copy.meta_primary_text || '').trim();
  if (meta) return meta;
  return String(proposal?.title || '')
    .replace(/^(trending|voorraadactie|seizoenspiek)\s*:\s*/i, '')
    .trim();
}

function todayIso() {
  return todayIsoAmsterdam();
}

export function proposalUsesIgComposer(proposal) {
  if (!proposal) return false;
  if (proposal.channel === 'meta') return true;
  return Boolean(proposal.product_id && proposal.channel === 'woo');
}

export default function MarketingProposalComposer({
  proposal,
  accountLabel,
  igAutoEnabled,
  defaultPublishTime,
  onPlanned,
  onDismiss,
}) {
  const handle = accountLabel?.replace(/\s+/g, '').toLowerCase() || '4shine';
  const [imageUrl, setImageUrl] = useState('');
  const [caption, setCaption] = useState(() => captionFromProposal(proposal));
  const [aiCopy, setAiCopy] = useState(proposal?.ai_copy_json || {});
  const [planDate, setPlanDate] = useState(() => {
    const s = proposal?.suggested_date;
    const t = todayIso();
    return s && s >= t ? s : t;
  });
  const [planIgAuto, setPlanIgAuto] = useState(proposal?.channel === 'meta');
  const [publishTime, setPublishTime] = useState(defaultPublishTime || '10:00');
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [gallery, setGallery] = useState([]);
  const [busy, setBusy] = useState(false);
  const [galleryLoading, setGalleryLoading] = useState(false);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [assetsLoading, setAssetsLoading] = useState(true);
  const triedAutoAi = useRef(false);

  useEffect(() => {
    setCaption(captionFromProposal(proposal));
    setAiCopy(proposal?.ai_copy_json || {});
    const s = proposal?.suggested_date;
    const t = todayIso();
    setPlanDate(s && s >= t ? s : t);
    setPlanIgAuto(proposal?.channel === 'meta');
    triedAutoAi.current = false;
  }, [proposal?.id, proposal?.suggested_date, proposal?.channel, proposal?.ai_copy_json, proposal?.title]);

  useEffect(() => {
    let cancelled = false;
    const day = String(planDate || '').slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return undefined;

    (async () => {
      try {
        const res = await api.get('/marketing/events', { params: { from: day, to: day } });
        const occupied = occupiedInstagramMinutesOnDay(res.data || [], day, defaultPublishTime || '10:00');
        const suggested = suggestPublishTimeForDay({
          dayIso: day,
          defaultTime: defaultPublishTime || '10:00',
          occupiedMinutes: occupied,
        });
        if (!cancelled) setPublishTime(suggested);
      } catch {
        if (!cancelled) setPublishTime(defaultPublishTime || '10:00');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [planDate, defaultPublishTime, proposal?.id]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!proposal?.product_id && !proposal?.product_ean) {
        setAssetsLoading(false);
        return;
      }
      setAssetsLoading(true);
      try {
        const params = proposal.product_id
          ? { product_id: proposal.product_id }
          : { ean: proposal.product_ean };
        const res = await api.get('/marketing/sfeer-image', { params });
        if (!cancelled && res.data?.image_url) setImageUrl(res.data.image_url);
      } catch {
        /* gallery / handmatig */
      } finally {
        if (!cancelled) setAssetsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [proposal?.id, proposal?.product_id, proposal?.product_ean]);

  const generateAiCaption = async () => {
    setAiGenerating(true);
    try {
      const res = await api.post(`/marketing/proposals/${proposal.id}/ai-copy`);
      const copy = res.data?.copy || {};
      const text = String(copy.meta_primary_text || '').trim();
      setAiCopy(copy);
      if (text) setCaption(text);
    } catch (e) {
      alert(e.response?.data?.error || e.message);
    } finally {
      setAiGenerating(false);
    }
  };

  useEffect(() => {
    if (triedAutoAi.current || assetsLoading) return;
    const meta = String(aiCopy?.meta_primary_text || '').trim();
    if (meta.length >= 15) return;
    triedAutoAi.current = true;
    generateAiCaption();
  }, [proposal.id, assetsLoading]);

  const loadGallery = async () => {
    setGalleryLoading(true);
    try {
      const res = await api.get(`/marketing/proposals/${proposal.id}/gallery-images`);
      setGallery(res.data?.images || []);
      setGalleryOpen(true);
    } catch (e) {
      alert(e.response?.data?.error || e.message);
    } finally {
      setGalleryLoading(false);
    }
  };

  const autoSfeer = async () => {
    setBusy(true);
    try {
      const params = proposal.product_id
        ? { product_id: proposal.product_id }
        : { ean: proposal.product_ean };
      const res = await api.get('/marketing/sfeer-image', { params });
      if (res.data?.image_url) {
        setImageUrl(res.data.image_url);
        setGallery((list) => list.map((g) => ({ ...g, selected: g.url === res.data.image_url })));
      }
    } catch (e) {
      alert(e.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  const saveDraftCopy = async () => {
    setBusy(true);
    try {
      const next = { ...aiCopy, meta_primary_text: caption.trim() };
      await api.patch(`/marketing/proposals/${proposal.id}`, { ai_copy_json: next });
      setAiCopy(next);
    } catch (e) {
      alert(e.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  const handlePlan = async () => {
    if (!planDate) {
      alert('Kies een datum');
      return;
    }
    setBusy(true);
    try {
      const nextCopy = { ...aiCopy, meta_primary_text: caption.trim() };
      await api.post(`/marketing/proposals/${proposal.id}/plan`, {
        starts_on: planDate,
        instagram_auto_publish: planIgAuto,
        approve_instagram: planIgAuto && igAutoEnabled,
        instagram_publish_time: publishTime.trim() || null,
        instagram_image_url: imageUrl.trim() || undefined,
        ai_copy_json: nextCopy,
      });
      onPlanned?.();
    } catch (e) {
      alert(e.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  const hasProduct = Boolean(proposal?.product_id || proposal?.product_ean);

  return (
    <div className="marketing-proposal-composer">
      <p className="muted marketing-proposal-composer-hint">
        Bouw je {proposal.channel === 'meta' ? 'Meta / Instagram' : 'shop'}-post — daarna inplannen in de kalender.
      </p>
      <article className="ig-preview-card ig-preview-draft marketing-proposal-ig-card">
        <header className="ig-preview-header">
          <div className="ig-preview-avatar" aria-hidden />
          <div>
            <strong className="ig-preview-handle">{handle}</strong>
            <span className="ig-preview-meta">Concept · plan {planDate}</span>
          </div>
          <span className="ig-preview-badge ig-preview-badge-draft">Voorstel</span>
        </header>

        <div className="ig-preview-media">
          {assetsLoading ? (
            <div className="ig-preview-media-placeholder">Afbeelding laden…</div>
          ) : imageUrl ? (
            <img src={imageUrl} alt="" />
          ) : (
            <div className="ig-preview-media-placeholder">Kies een foto uit de shop</div>
          )}
        </div>

        {hasProduct && (
          <>
            <div className="ig-preview-image-tools">
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                disabled={busy || galleryLoading}
                onClick={() => (galleryOpen ? setGalleryOpen(false) : loadGallery())}
              >
                <FiImage /> {galleryOpen ? 'Gallery verbergen' : 'Foto uit shop'}
              </button>
              <button type="button" className="btn btn-secondary btn-sm" disabled={busy} onClick={autoSfeer}>
                Auto sfeer
              </button>
            </div>
            {galleryOpen && gallery.length > 0 && (
              <div className="ig-preview-gallery">
                {gallery.map((img) => (
                  <button
                    key={img.gallery_index}
                    type="button"
                    className={`ig-preview-gallery-thumb${img.url === imageUrl ? ' selected' : ''}`}
                    onClick={() => setImageUrl(img.url)}
                  >
                    <img src={img.url} alt="" loading="lazy" />
                    <span>{img.gallery_index + 1}</span>
                  </button>
                ))}
              </div>
            )}
          </>
        )}

        <div className="ig-preview-edit-block">
          <label className="ig-preview-label">
            Caption (Instagram / Meta)
            <textarea
              className="form-input ig-preview-caption-input"
              rows={4}
              value={caption}
              disabled={busy || aiGenerating}
              onChange={(e) => setCaption(e.target.value)}
              placeholder="Publieke tekst…"
            />
          </label>
          <div className="ig-preview-caption-actions">
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              disabled={busy || aiGenerating}
              onClick={generateAiCaption}
            >
              <FiZap /> {aiGenerating ? 'AI…' : 'AI-caption'}
            </button>
            <button type="button" className="btn btn-secondary btn-sm" disabled={busy} onClick={saveDraftCopy}>
              <FiSave /> Concept opslaan
            </button>
          </div>
        </div>

        <div className="ig-preview-caption ig-preview-caption-readonly">
          <strong>{handle}</strong> <span className="ig-preview-caption-text">{caption || '—'}</span>
        </div>
      </article>

      <div className="marketing-proposal-composer-plan">
        <label className="marketing-plan-label">
          Plan op datum
          <input type="date" className="form-input" value={planDate} onChange={(e) => setPlanDate(e.target.value)} />
        </label>
        <label className="marketing-plan-label">
          Posttijd
          <input
            type="time"
            className="form-input"
            value={publishTime}
            onChange={(e) => setPublishTime(e.target.value)}
          />
          <span className="muted marketing-plan-time-hint">
            Voorstel vermijdt overlap (~1 uur) met andere geplande Instagram-posts op deze dag.
          </span>
        </label>
        <label className="marketing-ig-checkbox marketing-plan-ig-auto">
          <input type="checkbox" checked={planIgAuto} onChange={(e) => setPlanIgAuto(e.target.checked)} />
          Automatische Instagram-post op deze datum
        </label>
        <div className="marketing-detail-actions">
          <button type="button" className="btn btn-primary" disabled={busy} onClick={handlePlan}>
            <FiCheck /> Inplannen
          </button>
          <button type="button" className="btn btn-secondary" disabled={busy} onClick={() => onDismiss?.(proposal.id)}>
            <FiX /> Verbergen
          </button>
        </div>
      </div>
    </div>
  );
}
